/**
 * Formatadores compartilhados de tempo/data.
 *
 * Centralizar aqui evita reimplementação em página por página.
 */

export function formatElapsedSec(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}min ${s.toString().padStart(2, "0")}s`;
}

export function formatHHMMSS(iso: string): string {
  const d = new Date(iso);
  return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}:${d.getSeconds().toString().padStart(2, "0")}`;
}

export function formatDateBR(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  const now = Date.now();
  const diffMin = Math.floor((now - ts) / 60_000);
  if (diffMin < 1) return "agora";
  if (diffMin < 60) return `há ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `há ${diffD}d`;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "numeric",
    month: "short",
  }).format(new Date(iso));
}

/** Formata moeda BR a partir de centavos. Usado APENAS em contextos
 *  fiscais/contábeis (relatórios admin, exportação, página de pricing
 *  futura). Em todo o resto da UI use `formatCredits()` que mostra
 *  ND Credits — moeda interna da plataforma. */
export function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

/**
 * Formata ND Credits a partir de centavos BRL armazenados no banco.
 *
 * Conversão: 1 R$ = 100 ND Credits, ou seja 1 BRL cent = 1 ND Credit.
 * Backend continua persistindo em BRL cents (cost_events.cost_cents,
 * companies.budget_monthly_cents) — esta função só faz a tradução pra
 * apresentação.
 *
 * Por que ND Credits e não R$ direto:
 *  - Distância psicológica entre uso e dinheiro real (padrão de mercado
 *    em produtos de IA: OpenAI, Anthropic, ElevenLabs, Apify)
 *  - Margem de plataforma fica embutida no preço de compra de créditos
 *  - Pacotes/promos ficam mais flexíveis ("ganhe 1.000 créditos" vence
 *    "ganhe R$ 10")
 *
 * Quando exibir R$:
 *  - Página de compra de créditos (pricing — não existe ainda)
 *  - Relatórios fiscais / nota fiscal
 *  - Painel admin interno do time ND
 */
export function formatCredits(brlCents: number): string {
  const credits = Math.round(brlCents);
  return `${credits.toLocaleString("pt-BR")} créditos`;
}

/** Versão curta sem a palavra "créditos" — pra contextos onde já existe label */
export function formatCreditsShort(brlCents: number): string {
  return Math.round(brlCents).toLocaleString("pt-BR");
}

/** Tokens em formato curto: 1.2k, 5.1M */
export function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString("pt-BR");
}
